import user from 'js/lib/user';

import type { AccountDropdownLink } from 'bundles/composable-page-header/types';

import _t from 'i18n!nls/composable-page-header';

/*
Returns default set of account dropdown links for use with AccountDropdownGridItemModule.
Most scenarios will want to use these links as they take into account the user's role and permissions.
Only special cases will omit these links from the account dropdown or use a custom set.

Input parameters map to data returned from the PageHeader.queryMyPageHeader query.
*/
const getDefaultAccountDropdownLinks = ({
  isDegreeOrEnterpriseLearner,
  managedThirdPartyOrganizations,
  canViewEducatorAdminLink,
  hasCheatingIncidents,
}: {
  isDegreeOrEnterpriseLearner?: boolean;
  managedThirdPartyOrganizations?: Array<{ slug: string; name: string }> | null;
  canViewEducatorAdminLink?: boolean | null;
  hasCheatingIncidents?: boolean | null;
}): Array<AccountDropdownLink> => {
  const isStaff = user.isAuthenticatedUser() && (user.get().is_staff || canViewEducatorAdminLink);
  const dropdownLinks: Array<AccountDropdownLink> = [];

  if (isDegreeOrEnterpriseLearner) {
    dropdownLinks.push({
      href: '/?skipBrowseRedirect=true',
      label: _t('My Courses'),
      name: 'my-courses',
    });
  }

  if (isStaff) {
    dropdownLinks.push({
      href: '/admin/',
      label: _t('Educator Admin'),
      name: 'admin',
    });
  }

  if (managedThirdPartyOrganizations?.length) {
    managedThirdPartyOrganizations.forEach(({ name, slug }: $TSFixMe) => {
      dropdownLinks.push({
        href: `/o/${slug}/admin`,
        label: `${name} ${_t('Admin')}`,
        name: slug,
      });
    });
  }

  dropdownLinks.push({
    href: '/account-profile',
    label: _t('Profile'),
    name: 'profile',
  });

  dropdownLinks.push({
    href: '/my-purchases',
    label: _t('My Purchases'),
    name: 'my-purchases',
  });

  if (hasCheatingIncidents) {
    dropdownLinks.push({
      href: '/integrity-portal',
      label: _t('Honor Code Dashboard'),
      name: 'integrity-portal',
    });
  }

  dropdownLinks.push({
    href: '/account-settings',
    label: _t('Settings'),
    name: 'account-settings',
  });

  dropdownLinks.push({
    href: '/updates',
    label: _t('Updates'),
    name: 'updates',
  });

  dropdownLinks.push({
    href: '/accomplishments',
    label: _t('Accomplishments'),
    name: 'accomplishments',
  });

  if (isStaff) {
    dropdownLinks.push({
      href: 'https://partner.coursera.help/hc',
      label: _t('Educator Resource Center'),
      name: 'partner-resource-center',
      openInNewWindow: true,
    });

    dropdownLinks.push({
      href: 'https://learner.coursera.help/hc',
      label: _t('Learner Help Center'),
      name: 'learner-help-center',
      openInNewWindow: true,
    });
  } else {
    dropdownLinks.push({
      href: 'https://learner.coursera.help/hc',
      label: _t('Help Center'),
      name: 'learner-help-center',
      openInNewWindow: true,
    });
  }

  return dropdownLinks;
};

export default getDefaultAccountDropdownLinks;
